// ============================================================================
// zoomRaster — nitidezza dell'immagine durante lo zoom
// ============================================================================
// Problema:
//   PrismaZoom applica lo zoom con `transform: translate3d(...) scale(z)` su
//   un div che tiene SEMPRE `will-change: transform`
//   (react-prismazoom/dist/esm/index.js:565; lo stile viene poi riscritto
//   imperativamente in `update()`, righe 59-60, quindi non è rimovibile via
//   props). Quel div è un layer di compositing: il suo contenuto viene
//   rasterizzato UNA volta in una texture e lo `scale()` la ingrandisce
//   interpolando → pagina sfocata anche quando il file originale ha
//   risoluzione più che sufficiente. Un resize della finestra invalida le
//   tile e forza il re-raster alla scala corrente: è l'unico motivo per cui
//   il difetto "si aggiusta da solo" ridimensionando il browser.
//
//   I due motori si comportano diversamente:
//   - Chromium ricava il raster scale dalla screen-space transform (che
//     include lo scale degli antenati) e lo ricalcola appena l'hint
//     `will-change: transform` sparisce;
//   - WebKit rasterizza il backing store al solo deviceScaleFactor,
//     IGNORANDO lo scale degli antenati, e promuove il layer già solo per la
//     presenza di una transform 3D. Su Safari togliere l'hint non basta:
//     va tolto anche il `translate3d`, e va neutralizzato quello permanente
//     della slide attiva (`.awssld--active`, react-awesome-slider), che è il
//     layer che contiene davvero l'<img>.
//
// Strategia:
//   Durante il gesto (pinch/wheel/slider/pan) si tiene tutto composito, così
//   resta fluido. SETTLE_DELAY ms dopo l'ultimo evento, se siamo zoomati, si
//   smontano i layer: transform 2D + will-change:auto sul container e classe
//   `zoom-settled` che neutralizza il 3D della slide attiva (zoomRaster.css).
//   Senza layer compositi entrambi i motori ridisegnano alla scala corrente.
//
//   Al primo evento successivo si torna allo stato composito: il transform 3D
//   lo riscrive PrismaZoom stesso al primo `update()` del gesto.
//
// Nota: il ref di PrismaZoom espone solo l'API imperativa (getZoom, zoomIn,
// zoomOut, …), non il nodo DOM → il nodo si recupera per id.
// ============================================================================

import "../assets/css/zoomRaster.css";

// Id applicato al div di PrismaZoom nei caroselli (uno solo montato per volta).
export const ZOOM_NODE_ID = "prisma-zoom";

const SETTLED_CLASS = "zoom-settled";

// Deve coprire la transizione CSS di PrismaZoom (animDuration, default 0.25s)
// più un margine per l'ultimo frame del gesto.
const SETTLE_DELAY = 350;

// translate3d(<x>, <y>, <z>) → catturiamo x e y per riscriverlo in 2D.
const TRANSLATE_3D = /translate3d\(([^,]+),\s*([^,]+),\s*[^)]*\)/;
const SCALE = /scale\(([\d.]+)\)/;

export const createZoomRasterSharpener = (
  nodeId = ZOOM_NODE_ID,
  settleDelay = SETTLE_DELAY
) => {
  let timeoutId = null;
  let cachedNode = null;

  const getNode = () => {
    if (cachedNode && cachedNode.isConnected) return cachedNode;
    cachedNode = document.getElementById(nodeId);
    return cachedNode;
  };

  // Stato composito: gesto in corso o in arrivo → massima fluidità.
  const wake = (el) => {
    el.classList.remove(SETTLED_CLASS);
    if (el.style.willChange !== "transform") el.style.willChange = "transform";
  };

  // Stato disegnato: zoom fermo → via i layer, il browser ridisegna nitido.
  const settle = (el) => {
    const transform = el.style.transform || "";
    const scale = parseFloat((SCALE.exec(transform) || [])[1] || "1");

    // A scala 1 la texture è già alla risoluzione giusta: non serve smontare
    // niente e teniamo il layer pronto per il gesto successivo.
    if (!(scale > 1.01)) {
      el.classList.remove(SETTLED_CLASS);
      return;
    }

    // `transition: none` evita che lo swap 3D→2D venga interpolato; la
    // riscrive PrismaZoom al primo update successivo.
    el.style.transition = "none";
    el.style.transform = transform.replace(TRANSLATE_3D, "translate($1, $2)");
    el.style.willChange = "auto";
    el.classList.add(SETTLED_CLASS);
  };

  return {
    // Da chiamare su onZoomChange / onPanChange di PrismaZoom e all'inizio di
    // un cambio pagina (onTransitionStart), così lo stato "settled" non è mai
    // attivo durante le transizioni animate dello slider.
    notifyActivity() {
      const el = getNode();
      if (!el) return;
      wake(el);
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        const node = getNode();
        if (node) settle(node);
      }, settleDelay);
    },
    dispose() {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
      cachedNode = null;
    }
  };
};
